import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'wouter'

import { useSignOut } from '@/hooks'

import { Button } from './Button'
import { ErrorBoundary } from './ErrorBoundary'
import { IcpFooter } from './IcpFooter'
import { LanguageSwitcher } from './LanguageSwitcher'
import { Logo } from './Logo'
import { LogoutIcon } from './Icons'
import { SiteTabs } from './SiteTabs'
import { ThemeToggle } from './ThemeToggle'
import styles from './extractShell.module.css'

export interface ExtractShellProps {
  children: ReactNode
}

/**
 * The extraction screen's own chrome: navigation, account controls, no sidebar.
 *
 * This exists so the daily extraction page does not inherit the console's
 * identity pool, scheduler, backups and incident tooling. It keeps the same
 * three section tabs as the public site and the console, so moving between them
 * is one click rather than a visit to the address bar.
 */
export function ExtractShell({ children }: ExtractShellProps) {
  const { t } = useTranslation('common')
  const [, navigate] = useLocation()
  const signOut = useSignOut()

  return (
    <div className={styles.shell}>
      <a className={styles.skipLink} href="#main">
        {t('nav.skipToContent')}
      </a>

      <header className={styles.header}>
        <div className={styles.headerTop}>
          <Link className={styles.brand} href="/extract" aria-label={t('nav.home')}>
            <span className={styles.brandMark}>
              <Logo size={22} />
            </span>
            <span>{t('app.name')}</span>
          </Link>
          <div className={styles.actions}>
            <LanguageSwitcher />
            <ThemeToggle />
            <Button
              size="sm"
              variant="ghost"
              icon={<LogoutIcon size={14} />}
              onClick={() => {
                void signOut().then(() => {
                  navigate('/login')
                })
              }}
            >
              {t('action.signOut')}
            </Button>
          </div>
        </div>
        <div className={styles.tabsBar}>
          <div className={styles.tabsInner}>
            <SiteTabs />
          </div>
        </div>
      </header>

      <main className={styles.content} id="main">
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>

      <IcpFooter />
    </div>
  )
}
